import { Button, Field } from '@qr/ui';
import { FileUpload } from './FileUpload.js';
import type { QrType } from '@qr/shared/types';

/**
 * Formularios del contenido de los codigos que no llevan a una web ajena.
 *
 * Una tarjeta de contacto o una red wifi se pueden meter directamente dentro
 * del QR, y asi lo hace casi todo el mundo. El problema es que entonces el
 * codigo impreso queda atado a esos datos para siempre: si cambia un telefono,
 * hay que reimprimir las tarjetas.
 *
 * Aqui el QR sigue apuntando al enlace corto y los datos viven en el servidor,
 * asi que se pueden corregir sin tocar lo impreso. Es la misma promesa del
 * producto aplicada a estos tipos.
 */

export type PayloadValue = Record<string, unknown>;

interface FormProps {
  readonly value: PayloadValue;
  readonly disabled?: boolean;
  readonly onChange: (value: PayloadValue) => void;
}

/** Tipos que tienen formulario propio, con su nombre y para que sirven. */
export const HOSTED_TYPES: {
  readonly type: Exclude<QrType, 'url' | 'text'>;
  readonly label: string;
  readonly hint: string;
}[] = [
  {
    type: 'vcard',
    label: 'Tarjeta de contacto',
    hint: 'Se guarda en la agenda del movil con un toque.',
  },
  {
    type: 'wifi',
    label: 'Red wifi',
    hint: 'Para que los clientes se conecten sin pedir la clave.',
  },
  {
    type: 'links',
    label: 'Lista de enlaces',
    hint: 'Varios destinos en una pagina, como un menu.',
  },
  {
    type: 'file',
    label: 'Archivo',
    hint: 'Una carta, un catalogo o unas tarifas en PDF.',
  },
];

export function PayloadForm({
  type,
  value,
  disabled = false,
  orgId = null,
  onChange,
}: FormProps & { readonly type: QrType; readonly orgId?: string | null }) {
  switch (type) {
    case 'file':
      return (
        <FileUpload orgId={orgId} value={value} disabled={disabled} onChange={onChange} />
      );
    case 'vcard':
      return <VCardForm value={value} disabled={disabled} onChange={onChange} />;
    case 'wifi':
      return <WifiForm value={value} disabled={disabled} onChange={onChange} />;
    case 'links':
      return <LinksForm value={value} disabled={disabled} onChange={onChange} />;
    default:
      return null;
  }
}

/** Devuelve el texto de un campo del contenido, sin romperse si falta. */
function texto(value: PayloadValue, campo: string): string {
  const v = value[campo];
  return typeof v === 'string' ? v : '';
}

function VCardForm({ value, disabled, onChange }: FormProps) {
  const set = (campo: string, v: unknown) => onChange({ ...value, [campo]: v });

  return (
    <div className="payload">
      <div className="payload__row">
        <Field
          label="Nombre"
          value={texto(value, 'firstName')}
          disabled={disabled}
          required
          maxLength={60}
          onChange={(e) => set('firstName', e.target.value)}
        />
        <Field
          label="Apellidos"
          value={texto(value, 'lastName')}
          disabled={disabled}
          maxLength={60}
          onChange={(e) => set('lastName', e.target.value)}
        />
      </div>

      <div className="payload__row">
        <Field
          label="Empresa"
          value={texto(value, 'organization')}
          disabled={disabled}
          maxLength={100}
          onChange={(e) => set('organization', e.target.value)}
        />
        <Field
          label="Cargo"
          value={texto(value, 'title')}
          disabled={disabled}
          maxLength={100}
          onChange={(e) => set('title', e.target.value)}
        />
      </div>

      <div className="payload__row">
        <Field
          label="Teléfono"
          type="tel"
          value={texto(value, 'phone')}
          disabled={disabled}
          maxLength={40}
          placeholder="+57 300 000 0000"
          onChange={(e) => set('phone', e.target.value)}
        />
        <Field
          label="Correo"
          type="email"
          value={texto(value, 'email')}
          disabled={disabled}
          maxLength={160}
          onChange={(e) => set('email', e.target.value)}
        />
      </div>

      <Field
        label="Sitio web"
        type="url"
        value={texto(value, 'website')}
        disabled={disabled}
        placeholder="https://tu-web.com"
        onChange={(e) => set('website', e.target.value)}
      />

      <Field
        label="Dirección"
        value={texto(value, 'address')}
        disabled={disabled}
        maxLength={200}
        onChange={(e) => set('address', e.target.value)}
      />

      <Field
        label="Nota"
        value={texto(value, 'note')}
        disabled={disabled}
        maxLength={500}
        hint="Opcional. Aparece debajo de los datos de contacto."
        onChange={(e) => set('note', e.target.value)}
      />
    </div>
  );
}

function WifiForm({ value, disabled, onChange }: FormProps) {
  const set = (campo: string, v: unknown) => onChange({ ...value, [campo]: v });
  const encryption = texto(value, 'encryption') || 'WPA';
  const abierta = encryption === 'nopass';

  return (
    <div className="payload">
      <Field
        label="Nombre de la red"
        value={texto(value, 'ssid')}
        disabled={disabled}
        required
        maxLength={32}
        hint="Tal y como aparece al buscar redes. Distingue mayúsculas."
        onChange={(e) => set('ssid', e.target.value)}
      />

      <div className="qr-field">
        <label className="qr-field__label" htmlFor="wifi-enc">
          Seguridad
        </label>
        <select
          id="wifi-enc"
          className="qr-field__input"
          value={encryption}
          disabled={disabled}
          onChange={(e) => {
            const enc = e.target.value;
            // Una red abierta no lleva contrasena: dejarla guardada solo
            // consigue que se muestre una clave que no sirve para nada.
            onChange({
              ...value,
              encryption: enc,
              ...(enc === 'nopass' ? { password: '' } : {}),
            });
          }}
        >
          <option value="WPA">WPA / WPA2 (lo habitual)</option>
          <option value="WEP">WEP (redes antiguas)</option>
          <option value="nopass">Abierta, sin contraseña</option>
        </select>
      </div>

      {!abierta ? (
        <Field
          label="Contraseña"
          value={texto(value, 'password')}
          disabled={disabled}
          maxLength={63}
          hint="Quien escanee el código la verá en pantalla para copiarla."
          onChange={(e) => set('password', e.target.value)}
        />
      ) : null}

      <label className="payload__check">
        <input
          type="checkbox"
          checked={value['hidden'] === true}
          disabled={disabled}
          onChange={(e) => set('hidden', e.target.checked)}
        />
        Es una red oculta
      </label>
    </div>
  );
}

interface Enlace {
  label: string;
  url: string;
}

function LinksForm({ value, disabled, onChange }: FormProps) {
  const links = Array.isArray(value['links']) ? (value['links'] as Enlace[]) : [];
  const set = (campo: string, v: unknown) => onChange({ ...value, [campo]: v });

  function actualizar(index: number, enlace: Enlace) {
    const next = [...links];
    next[index] = enlace;
    set('links', next);
  }

  return (
    <div className="payload">
      <Field
        label="Título"
        value={texto(value, 'title')}
        disabled={disabled}
        required
        maxLength={80}
        placeholder="Restaurante La Plaza"
        onChange={(e) => set('title', e.target.value)}
      />

      <Field
        label="Descripción"
        value={texto(value, 'bio')}
        disabled={disabled}
        maxLength={300}
        hint="Opcional. Una línea bajo el título."
        onChange={(e) => set('bio', e.target.value)}
      />

      <div className="payload__links">
        <span className="qr-field__label">Enlaces</span>

        {links.length === 0 ? (
          <p className="muted small">Añade al menos uno.</p>
        ) : (
          <ul>
            {links.map((link, index) => (
              // El indice como clave es aceptable aqui: la lista solo se
              // reordena al anadir o quitar, y los campos se reescriben
              // enteros en cada cambio.
              <li key={index} className="payload__link">
                <Field
                  label="Texto del botón"
                  value={link.label ?? ''}
                  disabled={disabled}
                  maxLength={60}
                  placeholder="Ver la carta"
                  onChange={(e) => actualizar(index, { ...link, label: e.target.value })}
                />
                <Field
                  label="Dirección"
                  type="url"
                  value={link.url ?? ''}
                  disabled={disabled}
                  placeholder="https://..."
                  onChange={(e) => actualizar(index, { ...link, url: e.target.value })}
                />
                <button
                  type="button"
                  className="rules__icon rules__icon--danger"
                  disabled={disabled}
                  aria-label={`Quitar el enlace ${index + 1}`}
                  onClick={() => set('links', links.filter((_, i) => i !== index))}
                >
                  &#10005;
                </button>
              </li>
            ))}
          </ul>
        )}

        <Button
          variant="secondary"
          size="sm"
          disabled={disabled || links.length >= 20}
          onClick={() => set('links', [...links, { label: '', url: '' }])}
        >
          Añadir enlace
        </Button>
      </div>
    </div>
  );
}

/**
 * Comprueba si el contenido tiene lo minimo para guardarse.
 *
 * Duplica a proposito lo imprescindible de la validacion del servidor, que es
 * la que manda: aqui solo sirve para no dejar pulsar un boton que iba a fallar.
 */
export function payloadListo(type: QrType, value: PayloadValue): boolean {
  switch (type) {
    case 'vcard':
      return texto(value, 'firstName').trim().length > 0;
    case 'wifi':
      return texto(value, 'ssid').trim().length > 0;
    case 'file':
      return typeof value['url'] === 'string' && value['url'].length > 0;
    case 'links': {
      const links = Array.isArray(value['links']) ? (value['links'] as Enlace[]) : [];
      return (
        texto(value, 'title').trim().length > 0 &&
        links.length > 0 &&
        links.every((l) => l.label?.trim() && l.url?.trim())
      );
    }
    default:
      return true;
  }
}

/** Valores iniciales de cada tipo, para que el formulario arranque coherente. */
export function payloadInicial(type: QrType): PayloadValue {
  switch (type) {
    case 'wifi':
      return { encryption: 'WPA', hidden: false };
    case 'links':
      return { links: [{ label: '', url: '' }] };
    default:
      return {};
  }
}
